import {
  Button,
  Flex,
  Heading,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  Portal,
  Text,
} from '@chakra-ui/react';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import { LoadingSpinner } from '@gamma/ui/progress';
import { useSearchParams } from 'react-router';
import { i18n } from '@investigator/shared/localization';
import { Alert } from '@gamma/ui/overlay';
import { useCallback } from 'react';
import {
  useEntitiesContext,
  SuppressionAction,
} from '@investigator/features/entities/context';
const { excludeEntity } = i18n.pages.entityDetails;

const {
  suppressMsg,
  unSuppressMsg,
  suppress,
  unSuppress,
  willReceiveAlerts,
  willNotReceiveAlerts,
} = excludeEntity;

interface SuppressEntityContentProps {
  onClose?: () => void;
  refetchMain?: boolean;
  excludedEntitiesLoading?: boolean;
  isSuppressed?: boolean;
}

export const SuppressEntityContent = ({
  onClose,
  refetchMain = false,
  excludedEntitiesLoading = false,
  isSuppressed: isSuppressedProp,
}: SuppressEntityContentProps) => {
  const { selectedEntity, suppression } = useEntitiesContext();
  const [, setSearchParams] = useSearchParams();

  // Get entity info and suppression state
  const entity_name = selectedEntity?.entityName;
  const alert_name = selectedEntity?.detectionData?.alert_name;
  // Use prop if provided (preferred), otherwise fall back to context
  const isSuppressed =
    isSuppressedProp ?? selectedEntity?.entityIsSuppressed ?? false;

  const suppressedMessage =
    entity_name && alert_name
      ? i18n.formatString(
          isSuppressed ? unSuppressMsg : suppressMsg,
          <Text as="strong">{entity_name}</Text>,
          <Text as="strong">{alert_name}</Text>,
        )
      : '';

  const handleSuppressClick = useCallback(async () => {
    if (!selectedEntity) return;

    const action = isSuppressed
      ? SuppressionAction.UNSUPPRESS
      : SuppressionAction.SUPPRESS;

    const result = await suppression.updateSuppression(action);

    if (result.success) {
      onClose && onClose();

      if (refetchMain) {
        setSearchParams((params) => {
          params.set('refetch_main', 'true');
          return params;
        });
      }
    }
  }, [
    selectedEntity,
    isSuppressed,
    suppression,
    onClose,
    refetchMain,
    setSearchParams,
  ]);

  const isLoading = excludedEntitiesLoading || !suppressedMessage;

  return (
    <Portal>
      <PopoverContent w="320px" data-testid="suppress-entity-popover-content">
        <PopoverCloseButton top={1}>
          <CloseSharpIcon size="sm" />
        </PopoverCloseButton>
        <PopoverHeader>
          <Heading
            textStyle="body-md-bold"
            data-testid="suppress-entity-popover-heading"
          >
            {isSuppressed ? unSuppress : suppress}
          </Heading>
        </PopoverHeader>
        <PopoverBody>
          {isLoading ? (
            <Flex justifyContent="center" py={4}>
              <LoadingSpinner size="md" />
            </Flex>
          ) : (
            <Text mb={2} textStyle="body-md">
              {suppressedMessage}
            </Text>
          )}
          {suppression.error && (
            <Alert status="error" variant="subtle">
              {suppression.error}
            </Alert>
          )}
          <Alert variant="subtle" status="warning">
            {isSuppressed ? willReceiveAlerts : willNotReceiveAlerts}
          </Alert>
        </PopoverBody>
        <PopoverFooter alignItems="end">
          <Flex justifyContent="end">
            <Button
              variant="solid"
              colorScheme="blue"
              isLoading={suppression.pendingUpdate || isLoading}
              data-testid="suppress-entity-submit"
              onClick={handleSuppressClick}
            >
              {isSuppressed ? unSuppress : suppress}
            </Button>
          </Flex>
        </PopoverFooter>
      </PopoverContent>
    </Portal>
  );
};
